/**
 * E2EE CRYPTO MODULE
 * Implements client-side encryption using:
 * - Argon2id (WASM) for key derivation
 * - AES-GCM (WebCrypto) for key wrapping and record encryption
 * - BIP39 for recovery mnemonic generation
 */

export const E2EE = {
  // Argon2 parameters (OWASP recommendations for interactive login)
  ARGON2_PARAMS: {
    time: 3, // iterations
    mem: 65536, // 64 MB
    parallelism: 4,
    hashLen: 32, // 256-bit key
    type: 2, // argon2id
  },

  // Load argon2-browser WASM module (bundled locally, no CDN dependency)
  _argon2Module: null,
  async loadArgon2() {
    if (this._argon2Module) return this._argon2Module;

    if (!window.argon2) {
      await new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = "/vendor/argon2-bundled.min.js";
        script.integrity = "sha384-XOR3aNvHciLPIf6r+2glkrmbBbLmIJ1EChMXjw8eBKBf8gE0rDq1TyUNuRdorOqi";
        script.crossOrigin = "anonymous";
        script.onload = resolve;
        script.onerror = () => reject(new Error("Failed to load Argon2 WASM"));
        document.head.appendChild(script);
      });
    }
    this._argon2Module = window.argon2;
    return this._argon2Module;
  },

  // Derive key using Argon2id
  async deriveKey(password, salt) {
    const argon2 = await this.loadArgon2();
    const result = await argon2.hash({
      pass: password,
      salt: salt,
      ...this.ARGON2_PARAMS,
    });
    const key = new Uint8Array(result.hash);
    // Zero the argon2 result buffer (defense-in-depth)
    if (result.hash instanceof Uint8Array) result.hash.fill(0);
    return key;
  },

  // Generate cryptographically secure random bytes
  randomBytes(length) {
    return crypto.getRandomValues(new Uint8Array(length));
  },

  // Generate master key (32 random bytes)
  generateMasterKey() {
    return this.randomBytes(32);
  },

  // Generate salt (16 bytes)
  generateSalt() {
    return this.randomBytes(16);
  },

  // BIP39 English wordlist (2048 words)
  // Loaded from CDN on demand
  _bip39Wordlist: null,
  // SHA-256 hash of the canonical BIP39 English wordlist for integrity verification
  _bip39ExpectedHash: "187db04a869dd9bc7be80d21a86497d692c0db6abd3aa8cb6be5d618ff757fae",
  _bip39Urls: [
    "/bip39-english.txt", // Local copy served by our own backend (preferred)
    "https://raw.githubusercontent.com/bitcoin/bips/master/bip-0039/english.txt",
  ],
  async loadBIP39Wordlist() {
    if (this._bip39Wordlist) return this._bip39Wordlist;

    let text = null;
    let lastError = null;
    for (const url of this._bip39Urls) {
      try {
        const response = await fetch(url);
        if (response.ok) {
          text = await response.text();
          break;
        }
      } catch (e) {
        lastError = e;
      }
    }
    if (!text) {
      throw lastError || new Error("Failed to load BIP39 wordlist from all sources");
    }

    // Verify integrity of fetched wordlist
    const encoder = new TextEncoder();
    const hashBuffer = await crypto.subtle.digest("SHA-256", encoder.encode(text.trim()));
    const hashHex = Array.from(new Uint8Array(hashBuffer)).map(b => b.toString(16).padStart(2, "0")).join("");
    if (hashHex !== this._bip39ExpectedHash) {
      throw new Error("BIP39 wordlist integrity check failed — possible tampering");
    }

    this._bip39Wordlist = text.trim().split("\n");

    if (this._bip39Wordlist.length !== 2048) {
      throw new Error("Invalid BIP39 wordlist");
    }
    return this._bip39Wordlist;
  },

  // Generate 12-word recovery mnemonic (128 bits of entropy)
  async generateMnemonic() {
    const wordlist = await this.loadBIP39Wordlist();

    // Generate 128 bits (16 bytes) of entropy
    const entropy = this.randomBytes(16);

    // Calculate checksum (first 4 bits of SHA256)
    const hashBuffer = await crypto.subtle.digest("SHA-256", entropy);
    const hashArray = new Uint8Array(hashBuffer);
    const checksumBits = hashArray[0] >> 4; // First 4 bits

    // Convert entropy + checksum to 11-bit groups (132 bits = 12 words)
    // Each word index is 11 bits
    let bits = "";
    for (const byte of entropy) {
      bits += byte.toString(2).padStart(8, "0");
    }
    bits += checksumBits.toString(2).padStart(4, "0");

    const words = [];
    for (let i = 0; i < 12; i++) {
      const index = parseInt(bits.slice(i * 11, (i + 1) * 11), 2);
      words.push(wordlist[index]);
    }

    return words.join(" ");
  },

  // Validate mnemonic
  async validateMnemonic(mnemonic) {
    const wordlist = await this.loadBIP39Wordlist();
    const words = mnemonic.trim().toLowerCase().split(/\s+/);

    if (words.length !== 12) return false;

    // Check all words are valid
    for (const word of words) {
      if (!wordlist.includes(word)) return false;
    }

    // Verify checksum
    let bits = "";
    for (const word of words) {
      const index = wordlist.indexOf(word);
      bits += index.toString(2).padStart(11, "0");
    }

    const entropyBits = bits.slice(0, 128);
    const checksumBits = bits.slice(128);

    // Convert entropy bits back to bytes
    const entropy = new Uint8Array(16);
    for (let i = 0; i < 16; i++) {
      entropy[i] = parseInt(entropyBits.slice(i * 8, (i + 1) * 8), 2);
    }

    // Calculate expected checksum
    const hashBuffer = await crypto.subtle.digest("SHA-256", entropy);
    const hashArray = new Uint8Array(hashBuffer);
    const expectedChecksum = (hashArray[0] >> 4).toString(2).padStart(4, "0");

    return checksumBits === expectedChecksum;
  },

  // Convert mnemonic to seed for key derivation (using PBKDF2 as per BIP39)
  async mnemonicToSeed(mnemonic) {
    const encoder = new TextEncoder();
    const mnemonicBuffer = encoder.encode(mnemonic.normalize("NFKD"));
    const saltBuffer = encoder.encode("mnemonic"); // BIP39 uses "mnemonic" + passphrase

    const key = await crypto.subtle.importKey(
      "raw",
      mnemonicBuffer,
      "PBKDF2",
      false,
      ["deriveBits"],
    );

    const bits = await crypto.subtle.deriveBits(
      {
        name: "PBKDF2",
        salt: saltBuffer,
        iterations: 2048,
        hash: "SHA-512",
      },
      key,
      512,
    );

    return new Uint8Array(bits);
  },

  // AES-GCM encrypt (wrap key or encrypt data)
  // Optional aad parameter binds ciphertext to a context (e.g. docId)
  async encrypt(key, plaintext, aad = null) {
    const iv = this.randomBytes(12); // 96-bit IV for AES-GCM

    const cryptoKey = await crypto.subtle.importKey(
      "raw",
      key,
      { name: "AES-GCM" },
      false,
      ["encrypt"],
    );

    const params = { name: "AES-GCM", iv };
    if (aad) {
      params.additionalData = typeof aad === "string" ? new TextEncoder().encode(aad) : aad;
    }

    const ciphertext = await crypto.subtle.encrypt(
      params,
      cryptoKey,
      plaintext,
    );

    // Return IV + ciphertext concatenated
    const result = new Uint8Array(iv.length + ciphertext.byteLength);
    result.set(iv);
    result.set(new Uint8Array(ciphertext), iv.length);
    return result;
  },

  // AES-GCM decrypt
  // Optional aad parameter — tries with AAD first, falls back to no-AAD for legacy data
  async decrypt(key, ciphertextWithIV, aad = null) {
    const iv = ciphertextWithIV.slice(0, 12);
    const ciphertext = ciphertextWithIV.slice(12);

    const cryptoKey = await crypto.subtle.importKey(
      "raw",
      key,
      { name: "AES-GCM" },
      false,
      ["decrypt"],
    );

    if (aad) {
      const additionalData = typeof aad === "string" ? new TextEncoder().encode(aad) : aad;
      try {
        const plaintext = await crypto.subtle.decrypt(
          { name: "AES-GCM", iv, additionalData },
          cryptoKey,
          ciphertext,
        );
        return new Uint8Array(plaintext);
      } catch {
        // Fallback: legacy data encrypted without AAD
        // TODO: REMOVE-AFTER-MIGRATION — Remove no-AAD fallback once all data re-encrypted with AAD
        console.warn("[E2EE] AAD decryption failed, falling back to no-AAD (legacy data)");
      }
    }

    const plaintext = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv },
      cryptoKey,
      ciphertext,
    );

    return new Uint8Array(plaintext);
  },

  // HKDF - derive record-specific keys from master key
  async deriveRecordKey(masterKey, recordId) {
    const encoder = new TextEncoder();
    const info = encoder.encode("record:" + recordId);

    const cryptoKey = await crypto.subtle.importKey(
      "raw",
      masterKey,
      "HKDF",
      false,
      ["deriveBits"],
    );

    const bits = await crypto.subtle.deriveBits(
      {
        name: "HKDF",
        salt: new Uint8Array(32), // Zero salt: safe per HKDF spec, info field provides key separation
        info,
        hash: "SHA-256",
      },
      cryptoKey,
      256,
    );

    return new Uint8Array(bits);
  },

  // Base64 encode/decode for transport
  toBase64(uint8Array) {
    // Handle large arrays without stack overflow
    let binary = "";
    const len = uint8Array.length;
    const chunkSize = 0x8000; // 32KB chunks
    for (let i = 0; i < len; i += chunkSize) {
      const chunk = uint8Array.subarray(i, Math.min(i + chunkSize, len));
      binary += String.fromCharCode.apply(null, chunk);
    }
    return btoa(binary);
  },

  fromBase64(base64) {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  },

  // Compute SHA-256 hash of a Uint8Array (for recovery verifier)
  async sha256(data) {
    const hashBuffer = await crypto.subtle.digest("SHA-256", data);
    return new Uint8Array(hashBuffer);
  },

  // ---- Login key (the server never receives the password) ----
  //
  // kekP = Argon2id(password, salt_p) unwraps the master key, so whoever
  // learns the password can decrypt the whole vault. The server therefore
  // only ever sees authKey = HKDF-SHA256(kekP, info=AUTH_KEY_INFO), a
  // one-way function of kekP: knowing it (or the server's Argon2id hash of
  // it) doesn't help recover kekP. salt_p comes from POST /api/auth/prelogin.
  AUTH_KEY_INFO: "orbital:v1:login-auth",
  AUTH_KEY_SALT: "orbital:v1",

  async deriveAuthKey(kekP) {
    const base = await crypto.subtle.importKey("raw", kekP, "HKDF", false, ["deriveBits"]);
    const bits = await crypto.subtle.deriveBits(
      {
        name: "HKDF",
        hash: "SHA-256",
        salt: new TextEncoder().encode(this.AUTH_KEY_SALT),
        info: new TextEncoder().encode(this.AUTH_KEY_INFO),
      },
      base,
      256,
    );
    return new Uint8Array(bits);
  },

  // One Argon2id run gives both the vault KEK and the login key.
  // Caller must zero kekP when done.
  async deriveLoginKeys(password, saltP) {
    const kekP = await this.deriveKey(password, saltP);
    const authKey = await this.deriveAuthKey(kekP);
    return { kekP, authKey };
  },

  // Unwrap the master key with an already-derived password KEK.
  async unwrapWithKek(envelope, kekP) {
    return this.decrypt(kekP, this.fromBase64(envelope.wrap_p));
  },

  // Create envelope for signup
  async createEnvelope(password, mnemonic, masterKey) {
    const { envelope, authKey } = await this.createEnvelopeWithAuthKey(password, mnemonic, masterKey);
    authKey.fill(0);
    return envelope;
  },

  // Create envelope for signup plus the login key derived from its salt_p
  // (registration sends both; the password itself never leaves the client).
  async createEnvelopeWithAuthKey(password, mnemonic, masterKey) {
    // Generate salts
    const saltP = this.generateSalt();
    const saltR = this.generateSalt();

    // Derive KEKs
    const { kekP, authKey } = await this.deriveLoginKeys(password, saltP);

    // For recovery, use mnemonic seed
    const mnemonicSeed = await this.mnemonicToSeed(mnemonic);
    const kekR = await this.deriveKey(
      this.toBase64(mnemonicSeed.slice(0, 32)), // Use first 32 bytes as password
      saltR,
    );

    // Wrap master key with both KEKs
    const wrapP = await this.encrypt(kekP, masterKey);
    const wrapR = await this.encrypt(kekR, masterKey);

    // C-1: Compute recovery verifier = SHA-256(kekR) for server-side verification
    const recoveryVerifier = await this.sha256(kekR);

    const envelope = {
      salt_p: this.toBase64(saltP),
      kdf_params_p: this.ARGON2_PARAMS,
      wrap_p: this.toBase64(wrapP),
      salt_r: this.toBase64(saltR),
      kdf_params_r: this.ARGON2_PARAMS,
      wrap_r: this.toBase64(wrapR),
      recovery_verifier: this.toBase64(recoveryVerifier),
      version: 1,
    };
    // Zero intermediate key material (defense-in-depth)
    kekP.fill(0);
    kekR.fill(0);
    mnemonicSeed.fill(0);
    recoveryVerifier.fill(0);
    return { envelope, authKey };
  },

  // Rebuild the envelope keeping the password wrapping UNTOUCHED, replacing
  // only the recovery-phrase wrapping (salt_r/wrap_r) and its verifier.
  //
  // Used by the logged-in "regenerate recovery phrase" action. PUT
  // /api/e2ee/envelope replaces only the recovery half of an existing
  // envelope and refuses (409) a request whose password fields differ from the
  // stored ones, so they MUST be sent back verbatim. We therefore copy
  // salt_p/kdf_params_p/wrap_p straight from the current (freshly fetched)
  // envelope and only mint new recovery material. The master key itself is
  // unchanged, so both the password and the new phrase still decrypt it — no
  // data loss.
  async regenerateRecoveryEnvelope(currentEnvelope, mnemonic, masterKey) {
    if (
      !currentEnvelope ||
      !currentEnvelope.salt_p ||
      !currentEnvelope.wrap_p ||
      !currentEnvelope.kdf_params_p
    ) {
      throw new Error(
        "Cannot regenerate recovery: current envelope is missing password wrapping",
      );
    }

    // New recovery material (mirrors createEnvelope's recovery half exactly).
    const saltR = this.generateSalt();
    const mnemonicSeed = await this.mnemonicToSeed(mnemonic);
    const kekR = await this.deriveKey(
      this.toBase64(mnemonicSeed.slice(0, 32)),
      saltR,
    );
    const wrapR = await this.encrypt(kekR, masterKey);
    const recoveryVerifier = await this.sha256(kekR);

    const envelope = {
      // Preserve password wrapping verbatim (see note above).
      salt_p: currentEnvelope.salt_p,
      kdf_params_p: currentEnvelope.kdf_params_p,
      wrap_p: currentEnvelope.wrap_p,
      // New recovery wrapping + verifier.
      salt_r: this.toBase64(saltR),
      kdf_params_r: this.ARGON2_PARAMS,
      wrap_r: this.toBase64(wrapR),
      recovery_verifier: this.toBase64(recoveryVerifier),
      version: 1,
    };

    // Zero intermediate key material (defense-in-depth)
    kekR.fill(0);
    mnemonicSeed.fill(0);
    recoveryVerifier.fill(0);
    return envelope;
  },

  // Unwrap master key using password
  async unwrapWithPassword(envelope, password) {
    const saltP = this.fromBase64(envelope.salt_p);
    const wrapP = this.fromBase64(envelope.wrap_p);

    const kekP = await this.deriveKey(password, saltP);
    try {
      return await this.decrypt(kekP, wrapP);
    } finally {
      kekP.fill(0);
    }
  },

  // Unwrap master key using recovery mnemonic
  async unwrapWithRecovery(envelope, mnemonic) {
    const saltR = this.fromBase64(envelope.salt_r);
    const wrapR = this.fromBase64(envelope.wrap_r);

    const mnemonicSeed = await this.mnemonicToSeed(mnemonic);
    const kekR = await this.deriveKey(
      this.toBase64(mnemonicSeed.slice(0, 32)),
      saltR,
    );
    try {
      return await this.decrypt(kekR, wrapR);
    } finally {
      kekR.fill(0);
      mnemonicSeed.fill(0);
    }
  },

  // Encrypt a record
  async encryptRecord(masterKey, recordId, data) {
    const dek = await this.deriveRecordKey(masterKey, recordId);
    const encoder = new TextEncoder();
    const plaintext = encoder.encode(JSON.stringify(data));
    const ciphertext = await this.encrypt(dek, plaintext);
    return {
      nonce: this.toBase64(ciphertext.slice(0, 12)),
      ciphertext: this.toBase64(ciphertext.slice(12)),
    };
  },

  // Decrypt a record
  async decryptRecord(masterKey, recordId, encryptedData) {
    const dek = await this.deriveRecordKey(masterKey, recordId);
    const nonce = this.fromBase64(encryptedData.nonce);
    const ciphertext = this.fromBase64(encryptedData.ciphertext);

    // Reconstruct IV + ciphertext
    const combined = new Uint8Array(nonce.length + ciphertext.length);
    combined.set(nonce);
    combined.set(ciphertext, nonce.length);

    const plaintext = await this.decrypt(dek, combined);
    const decoder = new TextDecoder();
    return JSON.parse(decoder.decode(plaintext));
  },
};
